import React from 'react'
import styled, { keyframes, css } from 'styled-components'
import { IconUser, IconCrown, IconShieldCheck, IconCircleCheck } from '@tabler/icons-react'
import { useUserList } from 'app/hooks/trade/userList'
import { useGroupFee } from 'app/hooks/adminUsers/groupFee'

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(102, 126, 234, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
  }
`

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`

const UserTagContainer = styled.div<{ $selected?: boolean; $isOnline?: boolean; $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  padding: 2px 4px;
  background: transparent;
  border: none;
  border-radius: 4px;
  transition: all 0.2s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  user-select: none;
  
  &:hover {
    transform: ${props => props.$clickable ? 'translateY(-1px)' : 'none'};
    background: ${props => props.$clickable 
      ? (props.$selected ? 'rgba(102, 126, 234, 0.08)' : 'rgba(0, 0, 0, 0.03)')
      : 'transparent'};
  }
  
  &:active {
    transform: ${props => props.$clickable ? 'scale(0.98)' : 'none'};
  }
`

const AvatarWrapper = styled.div<{ $level?: number; $isOnline?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${props => {
    if (props.$level !== undefined) {
      const hue = (props.$level * 40) % 360
      return `linear-gradient(135deg, hsl(${hue}, 70%, 50%) 0%, hsl(${hue + 30}, 70%, 40%) 100%)`
    }
    return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  
  svg {
    color: white;
    width: 14px;
    height: 14px;
  }
`

const OnlineIndicator = styled.div`
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 7px;
  height: 7px;
  background: #10b981;
  border: 1.5px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  z-index: 1;
`

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  flex: 1;
  align-items: flex-start;
  justify-content: center;
`

const UserName = styled.div`
  color: rgba(20, 20, 20, 1);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  text-align: left;
  letter-spacing: -0.2px;
`

const UserDetail = styled.div`
  color: rgba(100, 100, 100, 0.9);
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  text-align: left;
  font-weight: 400;
`

const LevelBadge = styled.div<{ $level: number }>`
  position: absolute;
  top: -4px;
  right: -4px;
  background: ${props => {
    const hue = (props.$level * 40) % 360
    return `linear-gradient(135deg, hsl(${hue}, 70%, 50%) 0%, hsl(${hue + 30}, 70%, 40%) 100%)`
  }};
  color: white;
  font-size: 8px;
  font-weight: 600;
  padding: 1px 3px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  z-index: 2;
  min-width: auto;
  text-align: center;
  white-space: nowrap;
  line-height: 1.2;
`

const StatusBadge = styled.div<{ $color?: string }>`
  padding: 2px 6px;
  background: ${props => props.$color || 'rgba(102, 126, 234, 0.1)'};
  border: 1px solid ${props => props.$color ? 'rgba(16, 185, 129, 0.3)' : 'rgba(102, 126, 234, 0.2)'};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: ${props => props.$color ? 'rgba(16, 185, 129, 1)' : 'rgba(102, 126, 234, 1)'};
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
  
  svg {
    width: 10px;
    height: 10px;
  }
`

const CompactContainer = styled.div<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  background: transparent;
  padding: 2px 4px;
  width: 100%;
  border-radius: 4px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$clickable ? 'rgba(0, 0, 0, 0.03)' : 'transparent'};
  }
`

interface ModernUserTagProps {
  userid: string
  nickname?: string
  email?: string
  phone?: string
  uid?: string
  onClick?: () => void
  selected?: boolean
  style?: React.CSSProperties
  level?: number
  compact?: boolean
  showStatus?: boolean
}

const ModernUserTag: React.FC<ModernUserTagProps> = ({
  userid,
  nickname = '',
  email = '',
  phone = '',
  uid = '',
  onClick,
  selected = false,
  style = {},
  level,
  compact = false,
  showStatus = true,
}) => {
  const { userlist } = useUserList()
  const { data: groupData } = useGroupFee()
  
  let displayName = userid
  let displayDetail = `ID: ${userid}`
  
  if (nickname && nickname !== email && nickname !== '') {
    displayName = nickname
    displayDetail = `ID: ${userid}`
  } else if (email && email !== '') {
    const emailPrefix = email.split('@')[0]
    displayName = emailPrefix
    displayDetail = `ID: ${userid}`
  } else if (phone && phone !== '') {
    const cleanPhone = phone.replace(/-/g, '')
    displayName = cleanPhone
    displayDetail = `ID: ${userid}`
  } else if (uid && uid !== '') {
    displayName = uid
    displayDetail = `ID: ${userid}`
  }
  
  const isOnline = userlist?.find((user: any) => user === userid)
  const levelInfo = groupData?.find((group: any) => group.level === level)
  const levelName = levelInfo?.name || `Level ${level}`
  
  const getAvatarIcon = () => {
    if (level !== undefined) {
      if (level >= 8) return <IconCrown />
      if (level >= 5) return <IconShieldCheck />
      if (level >= 3) return <IconCircleCheck />
    }
    return <IconUser />
  }
  
  if (compact) {
    return (
      <CompactContainer style={style} onClick={onClick} $clickable={!!onClick}>
        <AvatarWrapper $level={level} $isOnline={isOnline}>
          {getAvatarIcon()}
          {isOnline && <OnlineIndicator />}
          {level !== undefined && <LevelBadge $level={level}>{levelName}</LevelBadge>}
        </AvatarWrapper>
        <UserName>{displayName}</UserName>
      </CompactContainer>
    )
  }
  
  return (
    <UserTagContainer
      $selected={selected}
      $isOnline={isOnline}
      $clickable={!!onClick}
      onClick={onClick}
      style={style}
    >
      <AvatarWrapper $level={level} $isOnline={isOnline}>
        {getAvatarIcon()}
        {isOnline && <OnlineIndicator />}
        {level !== undefined && <LevelBadge $level={level}>{level}</LevelBadge>}
      </AvatarWrapper>
      
      <UserInfo>
        <UserName>{displayName}</UserName>
        {displayDetail && <UserDetail>{displayDetail}</UserDetail>}
      </UserInfo>
      
      {showStatus && (
        <>
          {isOnline && (
            <StatusBadge $color="rgba(16, 185, 129, 0.2)">
              <IconCircleCheck />
            </StatusBadge>
          )}
          {level !== undefined && levelName && (
            <StatusBadge>
              {levelName}
            </StatusBadge>
          )}
        </>
      )}
    </UserTagContainer>
  )
}

export default React.memo(ModernUserTag)